import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  orderBy,
  increment,
  serverTimestamp,
  onSnapshot,
  setDoc
} from "firebase/firestore";
import { db, storage } from "../auth";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL
} from "firebase/storage";
import "./AdminDashboard.css";
import {
  IoWarningOutline,
  IoCheckmarkCircleOutline,
  IoTrashOutline,
  IoCloudUploadOutline
} from "react-icons/io5";
import { AiOutlineHistory } from "react-icons/ai";
import editvibelogo1 from "../assets/Editvibelogo1.svg";

// helper to format length
function formatLength(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// helper to format elapsed
function formatElapsed(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

const AdminDashboard = ({ user }) => {
  const [activeView, setActiveView] = useState("dashboard");
  const [usersList, setUsersList] = useState([]);
  const [creditInputs, setCreditInputs] = useState({});
  const [videosList, setVideosList] = useState([]);
  const [finalUploads, setFinalUploads] = useState({});
  const fileInputRefs = useRef({});
  const [durations, setDurations] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [hiddenVideos, setHiddenVideos] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("hiddenVideos") || "[]");
    } catch {
      return [];
    }
  });

  // —— GOD MODE state & subscription —— //
  const [uploadDisabled, setUploadDisabled] = useState(false);
  useEffect(() => {
    const cfgDoc = doc(db, "settings", "godMode");
    const unsub = onSnapshot(cfgDoc, snap => {
      setUploadDisabled(snap.exists() ? snap.data().uploadDisabled : false);
    });
    return unsub;
  }, []);

  const toggleGodMode = async () => {
    // 1) Optimistically flip UI
    const next = !uploadDisabled;
    setUploadDisabled(next);
 
    // 2) Persist to Firestore
    try {
      const cfgDoc = doc(db, "settings", "godMode");
      await setDoc(
        cfgDoc,
        { uploadDisabled: next },
        { merge: true }
      );
      console.log("God Mode set to", next);
    } catch (err) {
      console.error("Failed to toggle God Mode:", err);
      // Roll back UI if write fails
      setUploadDisabled(!next);
      alert("Could not update kill-switch. Check your permissions.");
    }
  };

  // —— “now” ticking for live timers —— //
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // helper to find email
  const findEmail = (userId) => {
    const u = usersList.find((u) => u.id === userId);
    return u?.email || userId;
  };

  // fetch users
  useEffect(() => {
    (async () => {
      const snap = await getDocs(query(collection(db, "users"), orderBy("createdAt")));
      setUsersList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  // fetch videos
  useEffect(() => {
    (async () => {
      const snap = await getDocs(
        query(collection(db, "videos"), orderBy("createdAt", "desc"))
      );
      setVideosList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  // load durations
  useEffect(() => {
    videosList.forEach((video) => {
      const id = video.id;
      const url = video.originalVideoURL || video.videoURL;
      if (url && durations[id] == null) {
        const vid = document.createElement("video");
        vid.preload = "metadata";
        vid.src = url;
        vid.onloadedmetadata = () => {
          setDurations(d => ({ ...d, [id]: vid.duration }));
        };
      }
    });
  }, [videosList, durations]);

  const handleInputChange = (userId, value) =>
    setCreditInputs(p => ({ ...p, [userId]: value }));

  const handleAddCredits = async (userId) => {
    const amt = parseInt(creditInputs[userId], 10);
    if (isNaN(amt)) return alert("Enter a valid number");
    await updateDoc(doc(db, "users", userId), {
      credits: increment(amt)
    });
    setCreditInputs(p => ({ ...p, [userId]: "" }));
  };

  const handleFinalFileChange = (videoId, file) =>
    setFinalUploads(p => ({ ...p, [videoId]: file }));

  const handleFinalUpload = async (videoDoc) => {
    const file = finalUploads[videoDoc.id];
    if (!file) return alert("Select a file first");
    const ext = file.name.split(".").pop();
    const name = `${videoDoc.userId}_final_${Date.now()}.${ext}`;
    const finalRef = ref(storage, `finalVideos/${videoDoc.userId}/${name}`);
    const task = uploadBytesResumable(finalRef, file);

    task.on(
      "state_changed",
      snap => {
        const pct = Math.floor(
          snap.bytesTransferred / snap.totalBytes * 100
        );
        setUploadProgress(p => ({ ...p, [videoDoc.id]: pct }));
      },
      err => {
        console.error(err);
        alert("Upload error");
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        await updateDoc(doc(db, "videos", videoDoc.id), {
          finalVideoName: file.name,
          finalVideoURL: url,
          status: "Final Uploaded",
          finalUploadedAt: serverTimestamp(),
        });
        setFinalUploads(p => ({ ...p, [videoDoc.id]: null }));
        setUploadProgress(p => ({ ...p, [videoDoc.id]: 0 }));
        // refresh
        const snap = await getDocs(
          query(collection(db, "videos"), orderBy("createdAt", "desc"))
        );
        setVideosList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    );
  };

  return (
    <div className="admin-dashboard-container">
      <aside className="admin-sidebar">
        <div className="sidebar-logo">
          <img src={editvibelogo1} alt="Logo" style={{ width: 140 }} />
        </div>
        <nav className="sidebar-nav">
          <ul>
            <li
              className={activeView === "dashboard" ? "active" : ""}
              onClick={() => setActiveView("dashboard")}
            >
              Dashboard
            </li>
            <li
              className={activeView === "users" ? "active" : ""}
              onClick={() => setActiveView("users")}
            >
              Users
            </li>
            <li
              className={activeView === "godmode" ? "active" : ""}
              onClick={() => setActiveView("godmode")}
            >
              God Mode
            </li>
          </ul>
        </nav>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <div />
          <div className="admin-avatar">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="Admin" />
            ) : (
              <div className="avatar-placeholder" />
            )}
          </div>
        </header>

        <div className="admin-content">
          {activeView === "dashboard" && (
            <>
              <h2 className="admin-page-title">Uploaded Videos</h2>
              <div className="admin-table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Length</th>
                      <th>Cost</th>
                      <th>Elapsed</th>
                      <th>Description</th>
                      <th>Music</th>
                      <th>SFX</th>
                      <th>Video File</th>
                      <th>Upload Final</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {videosList
                      .filter(v => !hiddenVideos.includes(v.id))
                      .map(videoDoc => {
                        const len = durations[videoDoc.id];
                        const cost = videoDoc.cost || 0;
                        const created = videoDoc.createdAt?.toDate() || new Date();
                        const finished = videoDoc.finalUploadedAt?.toDate?.();
                        const end = finished || now;
                        const elapsed = end - created;
                        return (
                          <tr key={videoDoc.id}>
                            <td title={videoDoc.userId} style={{ display: "flex", alignItems: "center" }}>
                              {videoDoc.finalVideoURL ? (
                                <IoCheckmarkCircleOutline color="green" />
                              ) : (
                                <IoWarningOutline color="orange" />
                              )}
                              &nbsp;{findEmail(videoDoc.userId)}
                            </td>
                            <td>{len != null ? formatLength(len) : "…"}</td>
                            <td>{cost}</td>
                            <td>{formatElapsed(elapsed)}</td>
                            <td>{videoDoc.description}</td>
                            <td>{videoDoc.music ? "Yes" : "No"}</td>
                            <td>{videoDoc.soundEffects ? "Yes" : "No"}</td>
                            <td>
                              <a href={videoDoc.videoURL} target="_blank" rel="noreferrer">
                                View
                              </a>
                            </td>
                            <td>
                              {videoDoc.finalVideoURL ? (
                                <IoTrashOutline
                                  style={{ cursor: "pointer" }}
                                  onClick={() => window.open(videoDoc.finalVideoURL, "_blank")}
                                />
                              ) : (
                                <>
                                  <input
                                    type="file"
                                    accept="video/*"
                                    ref={el => (fileInputRefs.current[videoDoc.id] = el)}
                                    onChange={e =>
                                      handleFinalFileChange(videoDoc.id, e.target.files[0])
                                    }
                                    style={{ display: "none" }}
                                  />
                                  {!finalUploads[videoDoc.id] ? (
                                    <IoCloudUploadOutline
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        fileInputRefs.current[videoDoc.id]?.click()
                                      }
                                    />
                                  ) : (
                                    <button onClick={() => handleFinalUpload(videoDoc)}>
                                      Upload
                                    </button>
                                  )}
                                  {uploadProgress[videoDoc.id] > 0 && (
                                    <div>
                                      <progress
                                        value={uploadProgress[videoDoc.id]}
                                        max="100"
                                      />
                                      &nbsp;{uploadProgress[videoDoc.id]}%
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {videoDoc.finalVideoURL && (
                                <IoTrashOutline
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        "Remove from UI permanently?"
                                      )
                                    ) {
                                      const updated = [
                                        ...hiddenVideos,
                                        videoDoc.id
                                      ];
                                      setHiddenVideos(updated);
                                      localStorage.setItem(
                                        "hiddenVideos",
                                        JSON.stringify(updated)
                                      );
                                    }
                                  }}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeView === "users" && (
            <>
              <h2 className="admin-page-title">Users</h2>
              <div className="admin-table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Credits</th>
                      <th>Add Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map(u => (
                      <tr key={u.id}>
                        <td>{u.email || "—"}</td>
                        <td>{u.plan}</td>
                        <td>{u.credits}</td>
                        <td>
                          <input
                            type="number"
                            value={creditInputs[u.id] || ""}
                            onChange={e => handleInputChange(u.id, e.target.value)}
                            style={{ width: 80, marginRight: 8 }}
                          />
                          <button onClick={() => handleAddCredits(u.id)}>Add</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeView === "godmode" && (
            <div className="godmode-container">
              <h2 className="admin-page-title">God Mode🕊️ (Kill Switch)</h2>
              <div className="toggle-row">
                <span>Prevent new uploads:</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={uploadDisabled}
                    onChange={toggleGodMode}
                  />
                  <span className="slider" />
                </label>
              </div>
              <p className="help-text">
                When enabled, all users attempting to upload will see a “service delayed” message.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;